import { definePlugin } from "prosekit/core";
import { Plugin, PluginKey, type Transaction } from "prosekit/pm/state";

// Same patterns as the InputRule versions, but without the $ anchor.
// Both patterns enforce safe URL schemes directly in the regex.
// Uses balanced parentheses matching to handle URLs with parens like .../P10_QE(2026)000877_EN.pdf
const IMAGE_PATTERN =
  /!\[([^\]]*)\]\(((https?|ftps?|file):\/\/[^()\s]*(?:\([^()\s]*\)[^()\s]*)*)\)/g;
const LINK_PATTERN =
  /\[([^\]\n]+)\]\(((https?|ftps?|file):\/\/[^()\n]*(?:\([^()\n]*\)[^()\n]*)*|(mailto|tel):[^()\n]*(?:\([^()\n]*\)[^()\n]*)*)\)/g;

/**
 * Auto-converts markdown image and link syntax when the cursor moves outside
 * the pattern. This complements the InputRule-based conversion which only fires
 * when the closing ) is typed as the last character.
 *
 * Use case: User types ![](), navigates back into () to type a URL,
 * then moves the cursor out — the pattern is converted to an image node.
 */
export function defineMarkdownAutoConvert() {
  return definePlugin(
    new Plugin({
      key: new PluginKey("markdown-auto-convert"),
      appendTransaction(transactions, _oldState, newState) {
        // Only check when selection changed but doc didn't —
        // doc changes are handled by InputRules
        const selectionChanged = transactions.some((tr) => tr.selectionSet);
        const docChanged = transactions.some((tr) => tr.docChanged);
        if (!selectionChanged || docChanged) return null;

        const { selection, doc, schema } = newState;
        const cursorPos = selection.from;

        // Only scan the paragraph the cursor is in (or just left)
        const $pos = selection.$from;
        const parentNode = $pos.parent;
        if (!parentNode.isTextblock || !parentNode.textContent) return null;
        const parentPos = $pos.before($pos.depth);
        const text = parentNode.textContent;

        let tr: Transaction | null = null;

        // Check for image patterns
        IMAGE_PATTERN.lastIndex = 0;
        let match;
        while ((match = IMAGE_PATTERN.exec(text)) !== null) {
          const from = parentPos + 1 + match.index; // +1 for the textblock node offset
          const to = from + match[0].length;

          // Only convert if cursor is outside the match.
          // Use < (not <=) for `to` because position `to` is right after the
          // closing ), which is outside the pattern.
          if (cursorPos >= from && cursorPos < to) continue;

          const [, alt, src] = match;
          if (!src) continue;
          if (doc.textBetween(from, to, "") !== match[0]) continue;

          const imageNode = schema.nodes.image.create({
            src: src.trim(),
            alt: alt || "",
          });

          tr = newState.tr.replaceWith(from, to, imageNode);
          break;
        }

        if (!tr) {
          // Check for link patterns
          LINK_PATTERN.lastIndex = 0;
          while ((match = LINK_PATTERN.exec(text)) !== null) {
            const from = parentPos + 1 + match.index;
            const to = from + match[0].length;

            if (cursorPos >= from && cursorPos < to) continue;

            const [, linkText, url] = match;
            if (!linkText || !url) continue;
            if (doc.textBetween(from, to, "") !== match[0]) continue;

            const linkMark = schema.marks.link.create({ href: url });
            const textNode = schema.text(linkText, [linkMark]);

            tr = newState.tr.replaceWith(from, to, textNode);
            break;
          }
        }

        return tr;
      },
    }),
  );
}
